import { useState, useEffect } from "react" ;
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  TrendingUp, 
  TrendingDown,
  Minus,
  Bookmark,
  ExternalLink,
  BarChart3,
  Calendar,
  Share2,
  Link2
} from "lucide-react";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrendDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [trend, setTrend] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);

/* eslint-disable-next-line react-hooks/exhaustive-deps */
useEffect(() => {
  fetchTrend();
  if (user) {
    fetchAnalytics();
    checkIfSaved();
  }
}, []);

  const fetchTrend = async () => {
    try {
      const response = await axios.get(`${API}/trends/${id}`);
      setTrend(response.data);
    } catch (error) {
      console.error("Error fetching trend:", error);
      toast.error("Trend not found");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/trends/${id}/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    }
  };

  const checkIfSaved = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/user/saved-trends`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const saved = response.data.saved_trends.some(s => s.trend.trend_id === id);
      setIsSaved(saved);
    } catch (error) {
      console.error("Error checking saved status:", error);
    }
  };

  const handleSave = async () => {
    if (!user) {
      toast.error("Please sign in to save trends");
      navigate("/auth");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/trends/${id}/save`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsSaved(response.data.saved);
      toast.success(response.data.saved ? "Trend saved!" : "Removed from saved");
    } catch (error) {
      toast.error("Failed to save trend");
    }
  };

  const handleShare = async () => {
    await navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied to clipboard!");
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num?.toString() || "0";
  };

  const getVelocityIcon = () => {
    if (!trend) return null;
    switch (trend.velocity) {
      case "rising":
        return <TrendingUp className="w-6 h-6 text-green-500" />;
      case "declining":
        return <TrendingDown className="w-6 h-6 text-red-500" />;
      default:
        return <Minus className="w-6 h-6 text-gray-400" />;
    }
  };

  const getPlatformColor = (platform) => {
    const colors = {
      tiktok: "#000000",
      youtube: "#FF0000",
      instagram: "#E1306C",
      pinterest: "#E60023",
      x: "#000000",
      facebook: "#1877F2",
      etsy: "#F16521",
      reddit: "#FF4500",
    };
    return colors[platform] || "#666666";
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="max-w-4xl mx-auto animate-pulse space-y-4">
          <div className="h-8 w-32 bg-gray-200" />
          <div className="h-12 w-3/4 bg-gray-200" />
          <div className="h-4 w-1/2 bg-gray-200" />
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!trend) return null;

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4 font-bold"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <span className="text-sm font-mono uppercase text-gray-500 mb-1 block">
                {trend.category}
              </span>
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tighter">
                {trend.keyword}
              </h1>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleShare}
                className="border-2 border-black"
                data-testid="share-btn"
              >
                <Share2 className="w-4 h-4" />
              </Button>
              <Button
                onClick={handleSave}
                className={`border-2 border-black font-bold ${
                  isSaved ? "bg-[#CCFF00] text-black" : "bg-black text-white"
                }`}
                data-testid="save-btn"
              >
                <Bookmark className={`w-4 h-4 mr-2 ${isSaved ? "fill-black" : ""}`} />
                {isSaved ? "Saved" : "Save"}
              </Button>
            </div>
          </div>

          {trend.description && (
            <p className="text-gray-600 text-lg mb-4">{trend.description}</p>
          )}

          {/* Tags */}
          {trend.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {trend.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-gray-100 text-sm font-mono"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          <div className="neo-card p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              {getVelocityIcon()}
            </div>
            <div className={`text-2xl font-bold ${
              trend.velocity === "rising" ? "text-green-600" : 
              trend.velocity === "declining" ? "text-red-600" : "text-gray-600"
            }`}>
              {trend.growth_rate > 0 ? "+" : ""}{trend.growth_rate?.toFixed(1)}%
            </div>
            <div className="text-xs text-gray-500 uppercase">Growth Rate</div>
          </div>
          <div className="neo-card p-4 text-center">
            <BarChart3 className="w-6 h-6 mx-auto mb-2 text-[#7C3AED]" />
            <div className="text-2xl font-bold">{formatNumber(trend.volume)}</div>
            <div className="text-xs text-gray-500 uppercase">Search Volume</div>
          </div>
          <div className="neo-card p-4 text-center">
            <Bookmark className="w-6 h-6 mx-auto mb-2 text-[#FF0099]" />
            <div className="text-2xl font-bold">{formatNumber(trend.saves)}</div>
            <div className="text-xs text-gray-500 uppercase">Saves</div>
          </div>
          <div className="neo-card p-4 text-center">
            <Calendar className="w-6 h-6 mx-auto mb-2 text-[#CCFF00]" />
            <div className="text-2xl font-bold capitalize">{trend.velocity}</div>
            <div className="text-xs text-gray-500 uppercase">Velocity</div>
          </div>
        </motion.div>

        {/* Platform Sources */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <h2 className="text-sm font-mono uppercase tracking-widest text-gray-500 mb-4">
            VIEW ON PLATFORMS
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {trend.platform_sources?.map((source, idx) => (
              <a
                key={idx}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="neo-card p-4 flex items-center gap-3 hover:bg-gray-50 transition-colors"
                style={{ borderLeftColor: getPlatformColor(source.platform), borderLeftWidth: 4 }}
              >
                <div>
                  <div className="font-bold capitalize">{source.platform}</div>
                  <div className="text-sm text-gray-500">
                    {formatNumber(source.engagement)} engagement
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 ml-auto text-gray-400" />
              </a>
            ))}
          </div>
        </motion.div>

        {/* Analytics Section (Premium/Logged in users) */}
        {user && analytics && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8"
          >
            <h2 className="text-sm font-mono uppercase tracking-widest text-gray-500 mb-4">
              ANALYTICS (30 DAY)
            </h2>
            
            {/* Mini Chart Representation */}
            <div className="neo-card p-6">
              <div className="flex items-end gap-1 h-32 mb-4">
                {analytics.daily_data?.slice(-14).map((day, idx) => {
                  const maxVolume = Math.max(...analytics.daily_data.map(d => d.volume));
                  const height = (day.volume / maxVolume) * 100;
                  return (
                    <div
                      key={idx}
                      className="flex-1 bg-[#CCFF00] hover:bg-[#FF0099] transition-colors"
                      style={{ height: `${height}%` }}
                      title={`${day.date}: ${formatNumber(day.volume)}`}
                    />
                  );
                })}
              </div>
              <div className="flex justify-between text-xs text-gray-500 font-mono">
                <span>14 days ago</span>
                <span>Today</span>
              </div>

              {/* Stats Summary */}
              <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
                <div>
                  <div className="text-sm text-gray-500">Peak Day</div>
                  <div className="font-bold">{analytics.peak_day}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Avg Growth</div>
                  <div className={`font-bold ${analytics.avg_growth >= 0 ? "text-green-600" : "text-red-600"}`}>
                    {analytics.avg_growth >= 0 ? "+" : ""}{analytics.avg_growth}%
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Top Platform</div>
                  <div className="font-bold capitalize">
                    {analytics.platform_breakdown?.[0]?.platform || "-"}
                  </div>
                </div>
              </div>
            </div>

            {/* Related Trends */}
            {analytics.related_trends?.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-mono uppercase tracking-widest text-gray-500 mb-3">
                  RELATED TRENDS
                </h3>
                <div className="flex flex-wrap gap-2">
                  {analytics.related_trends.map((related) => (
                    <Link
                      key={related}
                      to={`/explore?search=${encodeURIComponent(related)}`}
                      className="px-3 py-2 border-2 border-black font-medium hover:bg-black hover:text-white transition-colors"
                    >
                      {related}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* CTA for non-logged users */}
        {!user && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="neo-card p-6 bg-[#CCFF00]/20 text-center"
          >
            <BarChart3 className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">Unlock Full Analytics</h3>
            <p className="text-gray-600 mb-4">
              Sign up free to see detailed trend analytics, historical data, and related trends.
            </p>
            <Link to="/auth">
              <Button className="bg-black text-white hover:bg-[#FF0099] border-2 border-black font-bold">
                Sign Up Free
              </Button>
            </Link>
          </motion.div>
        )}

        {/* Affiliate CTA for Premium Users */}
        {user?.is_premium && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="neo-card p-6 bg-[#FF0099]/10 border-[#FF0099]"
          >
            <div className="flex items-center gap-4">
              <Link2 className="w-10 h-10 text-[#FF0099]" />
              <div className="flex-1">
                <h3 className="font-bold">Create Affiliate Link</h3>
                <p className="text-sm text-gray-600">
                  Found a product related to this trend? Create an affiliate link and earn 10% commission.
                </p>
              </div>
              <Link to="/affiliate">
                <Button className="bg-[#FF0099] text-white border-2 border-black font-bold">
                  Go to Affiliate
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default TrendDetailPage;
